import React, { useRef } from 'react'
import { Box, Button, Typography, CircularProgress, Chip } from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import numberToWords from 'number-to-words'
import currencyCodes from 'currency-codes'
import LogoP1 from '../../../assets/images/logomain.png'

/**
 * Invoice PDF Preview Component
 * Renders invoice in PDF-format that is indistinguishable from PlantBusinessView
 */
const InvoicePdfPreview = ({ invoice, organizationData, loading, id = 'invoice-pdf-preview' }) => {
  const printRef = useRef(null)
  const [downloading, setDownloading] = React.useState(false)


  const getCurrencyFullName = (currencyCode) => {
    const currency = currencyCodes.code(currencyCode || 'INR')
    return currency ? currency.currency : 'Rupees'
  }

  const handleDownload = async () => {
    if (!printRef.current) return

    setDownloading(true)
    try {
      const content = printRef.current

      // Hide specific UI elements for clean rendering (matches pdfGenerator.js)
      const editButtons = content.querySelectorAll('.MuiIconButton-root')
      editButtons.forEach((button) => (button.style.display = 'none'))

      const actionColumns = content.querySelectorAll('.action-column')
      actionColumns.forEach((col) => (col.style.display = 'none'))

      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
        imageTimeout: 0,
        removeContainer: true,
        letterRendering: true,
      })

      const imgData = canvas.toDataURL('image/jpeg', 1.0)
      const pdf = new jsPDF('p', 'mm', 'a4')

      const elementWidth = content.getBoundingClientRect().width
      const elementHeight = content.getBoundingClientRect().height
      const scaleFactor = pdf.internal.pageSize.getWidth() / elementWidth

      pdf.addImage(
        imgData,
        'JPEG',
        0,
        0,
        pdf.internal.pageSize.getWidth(),
        elementHeight * scaleFactor
      )

      // Revert UI changes
      editButtons.forEach((button) => (button.style.display = 'inline'))
      actionColumns.forEach((col) => (col.style.display = 'inline'))

      pdf.save(`Invoice_${invoice?.invoiceNumber || 'unknown'}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (!invoice) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography color="text.secondary">No invoice data available</Typography>
      </Box>
    )
  }

  // Formatting data like in InvoiceTable.jsx
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const invoiceDateParsed = dayjs(invoice.invoiceDate)
  const invoiceMonth = months[invoiceDateParsed.month()]
  const invoiceYear = invoiceDateParsed.format('YYYY')

  // Logic from PlantBusinessView
  const isLocationMatched = false // Hardcoded to false in PlantBusinessView
  const isEMIType = false // Could be derived if needed, keeping as false for now

  const subTotal = invoice.invoiceValue || 0
  const roundedTotal = Math.round(subTotal)
  const roundingAmount = roundedTotal - subTotal

  const mainEnergyValue = parseFloat(invoice.energyValue || 0)
  const mainTariff = parseFloat(invoice.mainTariff || invoice.tariff || 0)
  const mainAmount = mainEnergyValue * mainTariff

  const deemedEnergyValue = parseFloat(invoice.deemedEnergy || invoice.deemedGeneration || 0)
  const deemedTariff = parseFloat(invoice.deemedTariff || 0)
  const deemedAmount = deemedEnergyValue * deemedTariff

  const totalBillableUnits = mainEnergyValue + deemedEnergyValue
  const plantSize = invoice.plant?.plantSizeKW || invoice.plantSize || invoice.plantCapacity || ''

  const hasGSTN = Boolean(organizationData?.gstNumber)
  const billingAddress = invoice.billingAddress || invoice.address || organizationData?.address || invoice.plant?.plantLocation || ''
  const bankInfo = organizationData?.businessInfo || {}
  const paymentStatus = invoice.paymentStatus || invoice.status || 'PENDING'

  const FetchCurrency = (currency) => {
    if (currency === 'INR') return '₹'
    else if (currency === 'USD') return '$'
    return currency || '₹'
  }

  // Styles from PlantBusinessView/components/InvoiceBill/styles.js
  const styles = {
    container: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '60px',
      width: '250mm', // Matches containerNewStyle
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '10px',
    },
    th: {
      backgroundColor: '#f2f2f2',
      padding: '8px',
      textAlign: 'left',
      border: '1px solid #989393ff',
    },
    thRow: {
      backgroundColor: '#f2f2f2',
      padding: '2px',
      textAlign: 'center',
      border: '1px solid #989393ff',
      fontSize: '13px',
    },
    td: {
      border: '1px solid #dddddd',
      padding: '10px',
    },
    toph: {
      border: '1px solid #dddddd',
      padding: '8px',
      textAlign: 'left',
      width: '50%',
    }
  }

  return (
    <Box>
      {/* Header buttons (Not in PDF) */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Chip label={paymentStatus} color={paymentStatus === 'PAID' ? 'success' : paymentStatus === 'UNPAID' ? 'error' : 'warning'} size="small" />
        <Chip label={invoice.emailSent ? 'Email Sent' : 'Email Not Sent'} color={invoice.emailSent ? 'success' : 'default'} size="small" variant={invoice.emailSent ? 'filled' : 'outlined'} />
        <Chip label={invoice.sentToZoho ? 'Zoho Synced' : 'Not Synced'} color={invoice.sentToZoho ? 'info' : 'default'} size="small" variant={invoice.sentToZoho ? 'filled' : 'outlined'} />
        <Button variant="contained" startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} onClick={handleDownload} disabled={downloading}>
          {downloading ? 'Downloading...' : 'Download PDF'}
        </Button>
      </Box>

      {/* PDF Content */}
      <Box id={id} ref={printRef} sx={styles.container}>
        {/* Logo and Header */}
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <img style={{ height: 70, width: 150 }} src={LogoP1} alt="DE logo" />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginLeft: 16 }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{organizationData?.organizationName || invoice.organizationName || ''}</div>
              <div style={{ fontSize: '16px' }}>{organizationData?.address || ''}</div>
              {hasGSTN && <div style={{ fontSize: '16px' }}>GSTIN: {organizationData?.gstNumber}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
            <div style={{ fontSize: '20px', color: 'gray' }}>Distributed Energy</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>TAX INVOICE</div>
          </div>
        </div>

        {/* Info Table */}
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.toph}>
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 4 }}>
                  <div style={{ minWidth: '50%' }}>Invoice</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: {invoice.invoiceNumber || 'N/A'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 4 }}>
                  <div style={{ minWidth: '50%' }}>Invoice Date</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: {invoice.invoiceDate ? dayjs(invoice.invoiceDate).format('DD/MM/YYYY') : 'N/A'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ minWidth: '50%' }}>Due Date</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: {invoice.dueDate ? dayjs(invoice.dueDate).format('DD/MM/YYYY') : 'N/A'}</div>
                </div>
              </td>
              <td style={styles.toph}>
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 4 }}>
                  <div style={{ minWidth: '50%' }}>Plant Name</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', wordWrap: 'break-word', whiteSpace: 'normal' }}>: {invoice.plantName || 'N/A'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ minWidth: '50%' }}>Solar Plant Size</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: ({plantSize}kWp)</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Address Table */}
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.td}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1b6de0', marginBottom: 8 }}>{invoice.plantName}</div>
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: 1.5 }}>{billingAddress}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Subject Table */}
        <table style={styles.table}>
          <tbody>
            <tr>
              <td style={styles.td}>
                <div>Subject :</div>
                <div>Summary of Charges for {invoiceMonth} {invoiceYear}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Charges Table */}
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '5%' }} rowSpan={2}>#</th>
              <th style={{ ...styles.th, width: hasGSTN ? '25%' : '35%' }} rowSpan={2}>Item & Description</th>
              {hasGSTN && <th style={{ ...styles.th, width: '12%' }} rowSpan={2}>HSN/SAC</th>}
              <th style={{ ...styles.th, textAlign: 'right', width: hasGSTN ? '12%' : '20%' }} rowSpan={2}>Energy (kWh)</th>
              <th style={{ ...styles.th, textAlign: 'right', width: hasGSTN ? '12%' : '20%' }} rowSpan={2}>Tariff</th>
              {hasGSTN && (
                <th style={styles.thRow} colSpan={2}>
                  {isLocationMatched ? 'CGST' : 'IGST'}
                </th>
              )}
              {!isEMIType && <th style={{ ...styles.th, textAlign: 'right', width: '12%' }} rowSpan={2}>Amount</th>}
            </tr>
            {hasGSTN && (
              <tr>
                <th style={styles.thRow}>%</th>
                <th style={styles.thRow}>Amt</th>
              </tr>
            )}
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>1</td>
              <td style={styles.td}>{invoice.plantName} ({plantSize}kWp)</td>
              {hasGSTN && <td style={styles.td}>27160000</td>}
              <td style={{ ...styles.td, textAlign: 'right' }}>{mainEnergyValue.toFixed(2)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{mainTariff.toFixed(2)}</td>
              {hasGSTN && (
                <>
                  <td style={{ ...styles.td, textAlign: 'center' }}>0.00</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>0.00</td>
                </>
              )}
              {!isEMIType && <td style={{ ...styles.td, textAlign: 'right' }}>{mainAmount.toFixed(2)}</td>}
            </tr>
            <tr>
              <td style={styles.td}>2</td>
              <td style={styles.td}>Deemed Generation</td>
              {hasGSTN && <td style={styles.td}>27160000</td>}
              <td style={{ ...styles.td, textAlign: 'right' }}>{deemedEnergyValue.toFixed(2)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>{deemedTariff.toFixed(2)}</td>
              {hasGSTN && (
                <>
                  <td style={{ ...styles.td, textAlign: 'center' }}>0.00</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>0.00</td>
                </>
              )}
              {!isEMIType && <td style={{ ...styles.td, textAlign: 'right' }}>{deemedAmount.toFixed(2)}</td>}
            </tr>
          </tbody>
        </table>

        {/* Footer info and Bank Details */}
        <table style={{ ...styles.table, marginTop: 0 }}>
          <tbody>
            <tr>
              <td style={{ ...styles.td, width: '50%', verticalAlign: 'top' }}>
                <div style={{ marginBottom: 8 }}>Total Billable Units (kWh) {totalBillableUnits.toFixed(2)}</div>
                <div>Total In Words</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{getCurrencyFullName(invoice.currency)} {numberToWords.toWords(roundedTotal)} {isEMIType ? 'Tariff Total' : 'Total'} Only</div>

                <table style={{ ...styles.table, marginTop: 16 }}>
                  <tbody>
                    <tr>
                      <td style={styles.td}>
                        <div>Name of Bank : <span style={{ fontWeight: 'bold' }}>{bankInfo.nameOfTheBank || 'N/A'}</span></div>
                        <div>Address of Bank : <span style={{ fontWeight: 'bold' }}>{bankInfo.addressOfTheBank || 'N/A'}</span></div>
                        <div>Account Number : <span style={{ fontWeight: 'bold' }}>{bankInfo.accountNumber || 'N/A'}</span></div>
                        <div>IFSC Code : <span style={{ fontWeight: 'bold' }}>{bankInfo.ifscCode || 'N/A'}</span></div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td style={{ ...styles.td, width: '50%', verticalAlign: 'top' }}>
                <div style={{ display: 'flex', flexDirection: 'row', minWidth: '50%', justifyContent: 'end', border: '0px solid #dddddd', textAlign: 'left' }}>
                  <div style={{ minWidth: '25%' }}>
                    <div>Sub Total</div>
                    {hasGSTN && (
                      <>
                        {isLocationMatched ? (
                          <>
                            <div>CGST (0%)</div>
                            <div>SGST (0%)</div>
                          </>
                        ) : (
                          <div>IGST (0%)</div>
                        )}
                      </>
                    )}
                    <div>Rounding</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>Total</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>Balance Due</div>
                  </div>
                  <div style={{ minWidth: '25%' }}>
                    <div>: {FetchCurrency(invoice.currency)} {subTotal.toFixed(2)}</div>
                    {hasGSTN && (
                      <>
                        {isLocationMatched ? (
                          <>
                            <div>: {FetchCurrency(invoice.currency)} 0.00</div>
                            <div>: {FetchCurrency(invoice.currency)} 0.00</div>
                          </>
                        ) : (
                          <div>: {FetchCurrency(invoice.currency)} 0.00</div>
                        )}
                      </>
                    )}
                    <div>: {FetchCurrency(invoice.currency)} {roundingAmount.toFixed(2)}</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: {FetchCurrency(invoice.currency)} {roundedTotal.toFixed(2)}</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>: {FetchCurrency(invoice.currency)} {roundedTotal.toFixed(2)}</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  )
}

export default InvoicePdfPreview
